import React, { useState } from "react"
import { Link } from "gatsby"
import classnames from "classnames"

interface Props {
  isTransparent?: boolean
}

const Navbar: React.FC<Props> = ({ isTransparent }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false)

  return (
    <div
      className={classnames("z-50", {
        "absolute w-full": isTransparent,
      })}
    >
      <nav className="px-4 py-5 mx-auto sm:max-w-xl md:max-w-full lg:max-w-screen-xl md:px-24 lg:px-8">
        <div className="relative flex items-center justify-between">
          <Link to="/" className="inline-flex items-center">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              className="w-8 text-green-600"
            >
              <path fill="none" d="M0 0h24v24H0z" />
              <path
                fill="currentColor"
                d="M5.5 21a4.5 4.5 0 1 1 0-9 4.5 4.5 0 0 1 0 9zm0-3a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3zm13 3a4.5 4.5 0 1 1 0-9 4.5 4.5 0 0 1 0 9zm0-3a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3zm-6.969-8.203L13 12v6h-2v-5l-2.719-2.266A2 2 0 0 1 8 7.671l2.828-2.828a2 2 0 0 1 2.829 0l1.414 1.414a6.969 6.969 0 0 0 3.917 1.975l-.01 2.015a8.962 8.962 0 0 1-5.321-2.575L11.53 9.797zM16 5a2 2 0 1 1 0-4 2 2 0 0 1 0 4z"
              />
            </svg>
            <span
              className={classnames(
                "ml-2 text-xl font-bold tracking-wide",
                {
                  "text-gray-100": isTransparent,
                },
                {
                  "text-gray-800": !isTransparent,
                }
              )}
            >
              Cycleworx Langebaan
            </span>
          </Link>
          <ul className="items-center hidden space-x-8 lg:flex">
            <li>
              <Link
                to="/about"
                activeClassName="text-green-600"
                className={classnames(
                  "font-medium tracking-wide transition-colors duration-200",
                  {
                    "text-gray-200 hover:text-green-600": isTransparent,
                  },
                  {
                    "text-gray-700 hover:text-green-500": !isTransparent,
                  }
                )}
              >
                About us
              </Link>
            </li>
            <li>
              <Link
                to="/contact"
                activeClassName="text-green-600"
                className={classnames(
                  "font-medium tracking-wide transition-colors duration-200",
                  {
                    "text-gray-200 hover:text-green-500": isTransparent,
                  },
                  {
                    "text-gray-700 hover:text-green-400": !isTransparent,
                  }
                )}
              >
                Contact us
              </Link>
            </li>
          </ul>
          <div className="lg:hidden">
            <button
              aria-label="Open Menu"
              title="Open Menu"
              className={classnames(
                "p-2 -mr-1 transition duration-200 rounded focus:outline-none focus:shadow-outline ",
                { "hover:bg-green-50 focus:bg-green-50": !isTransparent }
              )}
              onClick={() => setIsMenuOpen(true)}
            >
              <svg
                className={classnames(
                  "w-5",
                  {
                    "text-gray-100": isTransparent,
                  },
                  {
                    "text-gray-600": !isTransparent,
                  }
                )}
                viewBox="0 0 24 24"
              >
                <path
                  fill="currentColor"
                  d="M23,13H1c-0.6,0-1-0.4-1-1s0.4-1,1-1h22c0.6,0,1,0.4,1,1S23.6,13,23,13z"
                />
                <path
                  fill="currentColor"
                  d="M23,6H1C0.4,6,0,5.6,0,5s0.4-1,1-1h22c0.6,0,1,0.4,1,1S23.6,6,23,6z"
                />
                <path
                  fill="currentColor"
                  d="M23,20H1c-0.6,0-1-0.4-1-1s0.4-1,1-1h22c0.6,0,1,0.4,1,1S23.6,20,23,20z"
                />
              </svg>
            </button>
            {isMenuOpen && (
              <div className="absolute top-0 left-0 z-50 w-full">
                <div className="p-5 bg-white border rounded shadow-sm">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <Link to="/" className="inline-flex items-center">
                        <svg
                          className="w-8 text-green-accent-400"
                          viewBox="0 0 24 24"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeMiterlimit="10"
                          stroke="currentColor"
                          fill="none"
                        >
                          <rect x="3" y="1" width="7" height="12" />
                          <rect x="3" y="17" width="7" height="6" />
                          <rect x="14" y="1" width="7" height="6" />
                          <rect x="14" y="11" width="7" height="12" />
                        </svg>
                        <span className="ml-2 text-xl font-bold tracking-wide text-gray-800">
                          Cycleworx Langebaan
                        </span>
                      </Link>
                    </div>
                    <div>
                      <button
                        aria-label="Close Menu"
                        title="Close Menu"
                        className="p-2 -mt-2 -mr-2 transition duration-200 rounded hover:bg-gray-200 focus:bg-gray-200 focus:outline-none focus:shadow-outline"
                        onClick={() => setIsMenuOpen(false)}
                      >
                        <svg className="w-5 text-gray-600" viewBox="0 0 24 24">
                          <path
                            fill="currentColor"
                            d="M19.7,4.3c-0.4-0.4-1-0.4-1.4,0L12,10.6L5.7,4.3c-0.4-0.4-1-0.4-1.4,0s-0.4,1,0,1.4l6.3,6.3l-6.3,6.3 c-0.4,0.4-0.4,1,0,1.4C4.5,19.9,4.7,20,5,20s0.5-0.1,0.7-0.3l6.3-6.3l6.3,6.3c0.2,0.2,0.5,0.3,0.7,0.3s0.5-0.1,0.7-0.3 c0.4-0.4,0.4-1,0-1.4L13.4,12l6.3-6.3C20.1,5.3,20.1,4.7,19.7,4.3z"
                          />
                        </svg>
                      </button>
                    </div>
                  </div>
                  <nav>
                    <ul className="space-y-4">
                      <li>
                        <Link
                          to="/about"
                          activeClassName="text-green-accent-400"
                          className="font-medium tracking-wide text-gray-700 transition-colors duration-200 hover:text-green-accent-400"
                        >
                          About us
                        </Link>
                      </li>
                      <li>
                        <Link
                          to="/contact"
                          activeClassName="text-green-accent-400"
                          className="font-medium tracking-wide text-gray-700 transition-colors duration-200 hover:text-green-accent-400"
                        >
                          Contact us
                        </Link>
                      </li>
                    </ul>
                  </nav>
                </div>
              </div>
            )}
          </div>
        </div>
      </nav>
    </div>
  )
}

export default Navbar
